import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CpmButton,
    CpmErrorCard,
    CpmPageShell,
    CpmVolumeTableSkeleton,
} from '../../../components/design-system';
import {
    CPM_VOLUME_SETTING_STATUS,
    formatFlaggedDate,
    volumeUnitShortLabel,
} from '../../../constants/centralProductMaster.constants';
import {
    createVolumeSetting,
    deleteVolumeSetting,
    listVolumeSettings,
    updateVolumeSetting,
} from '../../../services/productMasterService';
import type { CpmVolumeSetting } from '../../../types/centralProductMaster.types';
import { toast } from '../../../utils/toast';
import DeleteVolumeConfirm from './components/DeleteVolumeConfirm';
import VolumeFormModal, { type VolumeFormValues } from './components/VolumeFormModal';

const GRID_COLS = '88px 60px 108px minmax(0,1fr) minmax(240px,auto)';

type RowPhase = 'opening' | 'toggling' | 'toggleError' | 'deleting';
type FormPhase = 'idle' | 'loading' | 'fetchError' | 'saving' | 'done';

function volumeLabel(v: Pick<CpmVolumeSetting, 'value' | 'unit'>): string {
    return `${Number(v.value).toLocaleString('en-IN')} ${volumeUnitShortLabel(v.unit)}`;
}

function SlidersIcon({ size = 19 }: { size?: number }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M4 21v-7" />
            <path d="M4 10V3" />
            <path d="M12 21v-9" />
            <path d="M12 8V3" />
            <path d="M20 21v-5" />
            <path d="M20 12V3" />
            <path d="M1 14h6" />
            <path d="M9 8h6" />
            <path d="M17 16h6" />
        </svg>
    );
}

export default function VolumeSettings() {
    const [settings, setSettings] = useState<CpmVolumeSetting[]>([]);
    const [boot, setBoot] = useState<'loading' | 'ready' | 'error'>('loading');
    const [bootRetrying, setBootRetrying] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [formPhase, setFormPhase] = useState<FormPhase>('idle');
    const [editing, setEditing] = useState<CpmVolumeSetting | null>(null);
    const [deleting, setDeleting] = useState<CpmVolumeSetting | null>(null);
    const [rowPhase, setRowPhase] = useState<Record<string, RowPhase | undefined>>({});
    const [addOpening, setAddOpening] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncedFlash, setSyncedFlash] = useState(false);
    const [persistError, setPersistError] = useState(false);
    const [persistRetrying, setPersistRetrying] = useState(false);
    const pendingToastRef = useRef<string | null>(null);
    const syncTimerRef = useRef<number | null>(null);

    const setRow = (id: string, phase: RowPhase | null) => {
        setRowPhase((prev) => {
            const next = { ...prev };
            if (phase == null) delete next[id];
            else next[id] = phase;
            return next;
        });
    };

    const markSyncedFlash = useCallback(() => {
        setSyncedFlash(true);
        if (syncTimerRef.current != null) window.clearTimeout(syncTimerRef.current);
        syncTimerRef.current = window.setTimeout(() => setSyncedFlash(false), 1600);
    }, []);

    const load = useCallback(async (mode: 'boot' | 'retry' | 'soft' = 'boot') => {
        if (mode === 'retry') setBootRetrying(true);
        if (mode === 'boot') setBoot('loading');
        try {
            const data = await listVolumeSettings();
            setSettings(data.settings ?? []);
            setBoot('ready');
            setPersistError(false);
            return true;
        } catch {
            if (mode === 'soft') {
                // Keep current list visible; page banner + toast for retry.
                setPersistError(true);
                toast.error("Couldn't refresh volume settings.");
            } else {
                setBoot('error');
            }
            return false;
        } finally {
            setBootRetrying(false);
        }
    }, []);

    const syncAfterWrite = useCallback(async () => {
        setSyncing(true);
        setSyncedFlash(false);
        setPersistError(false);
        const ok = await load('soft');
        setSyncing(false);
        if (ok) markSyncedFlash();
        return ok;
    }, [load, markSyncedFlash]);

    const retryPersist = async () => {
        if (persistRetrying) return;
        setPersistRetrying(true);
        await syncAfterWrite();
        setPersistRetrying(false);
    };

    useEffect(() => {
        void load('boot');
        return () => {
            if (syncTimerRef.current != null) window.clearTimeout(syncTimerRef.current);
        };
    }, [load]);

    const rows = useMemo(
        () =>
            settings
                .slice()
                .sort(
                    (a, b) =>
                        Number(a.value) - Number(b.value) ||
                        String(a.unit).localeCompare(String(b.unit))
                ),
        [settings]
    );

    const activeCount = useMemo(
        () => rows.filter((r) => r.status === CPM_VOLUME_SETTING_STATUS.ACTIVE).length,
        [rows]
    );

    const summary = rows.length
        ? `${rows.length} ${rows.length === 1 ? 'volume' : 'volumes'} · ${activeCount} active · ${rows.length - activeCount} inactive`
        : 'No volumes configured';

    const openAdd = () => {
        if (addOpening) return;
        setAddOpening(true);
        window.setTimeout(() => {
            setEditing(null);
            setFormPhase('idle');
            setFormOpen(true);
            setAddOpening(false);
        }, 220);
    };

    const openEdit = (row: CpmVolumeSetting) => {
        if (rowPhase[row._id]) return;
        setRow(row._id, 'opening');
        setFormPhase('loading');
        setEditing(null);
        setFormOpen(true);
        window.setTimeout(() => {
            const still = settings.find((s) => s._id === row._id);
            setRow(row._id, null);
            if (!still) {
                setFormPhase('fetchError');
                setEditing(row);
                return;
            }
            setEditing(still);
            setFormPhase('idle');
        }, 420);
    };

    const retryFormFetch = () => {
        if (!editing) return;
        openEdit(editing);
    };

    const closeForm = () => {
        setFormOpen(false);
        setEditing(null);
        setFormPhase('idle');
        if (pendingToastRef.current) {
            toast.success(pendingToastRef.current);
            pendingToastRef.current = null;
        }
    };

    const findDuplicate = (
        value: number,
        unit: string,
        excludeId?: string
    ): CpmVolumeSetting | undefined =>
        settings.find(
            (v) =>
                Number(v.value) === value && v.unit === unit && (!excludeId || v._id !== excludeId)
        );

    const handleSave = async (values: VolumeFormValues): Promise<string | null> => {
        const dup = findDuplicate(values.value, values.unit, editing?._id);
        if (dup) {
            const short = volumeUnitShortLabel(values.unit);
            const base = `${values.value.toLocaleString('en-IN')} ${short} is already in the list`;
            return dup.status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                ? `${base} as Inactive — edit that entry to make it Active again.`
                : `${base}. Each value and unit pair can only appear once.`;
        }

        const label = `${values.value.toLocaleString('en-IN')} ${volumeUnitShortLabel(values.unit)}`;
        try {
            if (editing) {
                const beforeStatus = editing.status;
                await updateVolumeSetting({
                    id: editing._id,
                    value: values.value,
                    unit: values.unit,
                    status: values.status,
                });
                const statusChanged = beforeStatus !== values.status;
                if (statusChanged) {
                    pendingToastRef.current =
                        values.status === CPM_VOLUME_SETTING_STATUS.INACTIVE
                            ? `${label} is now Inactive — it no longer appears in the Variant create dropdown. Existing variants are unchanged.`
                            : `${label} is Active again and selectable on create.`;
                } else {
                    pendingToastRef.current = `Updated to ${label}.`;
                }
            } else {
                await createVolumeSetting({ value: values.value, unit: values.unit });
                pendingToastRef.current = `Added ${label} as Active — it is now selectable when creating a variant.`;
            }
            await syncAfterWrite();
            return null;
        } catch {
            return `Couldn't save ${label}. The change was not applied — try again.`;
        }
    };

    const handleToggleStatus = async (row: CpmVolumeSetting) => {
        const phase = rowPhase[row._id];
        if (phase === 'toggling' || phase === 'deleting') return;
        const on = row.status === CPM_VOLUME_SETTING_STATUS.ACTIVE;
        const next = on ? CPM_VOLUME_SETTING_STATUS.INACTIVE : CPM_VOLUME_SETTING_STATUS.ACTIVE;
        setRow(row._id, 'toggling');
        try {
            await updateVolumeSetting({ id: row._id, status: next });
            setRow(row._id, null);
            toast.success(
                on
                    ? `${volumeLabel(row)} is now Inactive — it stops appearing in the Variant create dropdown. Existing variants keep their volume.`
                    : `${volumeLabel(row)} is Active again and selectable on create.`
            );
            await syncAfterWrite();
        } catch {
            setRow(row._id, 'toggleError');
            toast.error(
                `Couldn't ${on ? 'disable' : 'enable'} ${volumeLabel(row)}. It is still ${on ? 'Active' : 'Inactive'} — nothing changed.`
            );
        }
    };

    const handleConfirmDelete = async (): Promise<string | null> => {
        if (!deleting) return 'Nothing to delete.';
        const row = deleting;
        setRow(row._id, 'deleting');
        try {
            await deleteVolumeSetting({ id: row._id });
            setRow(row._id, null);
            setDeleting(null);
            toast.success(
                `Deleted ${volumeLabel(row)}. It is no longer offered on create; existing variants keep their volume.`
            );
            await syncAfterWrite();
            return null;
        } catch {
            setRow(row._id, null);
            return `Couldn't delete ${volumeLabel(row)}. It is still in the list — try again.`;
        }
    };

    const handleDisableInstead = async (usageCount: number): Promise<string | null> => {
        if (!deleting) return 'Nothing to disable.';
        const row = deleting;
        try {
            await updateVolumeSetting({
                id: row._id,
                status: CPM_VOLUME_SETTING_STATUS.INACTIVE,
            });
            setDeleting(null);
            toast.success(
                `${volumeLabel(row)} disabled. Its ${usageCount.toLocaleString('en-IN')} existing variants keep their volume; it is no longer offered on create.`
            );
            await syncAfterWrite();
            return null;
        } catch {
            return `Couldn't disable ${volumeLabel(row)}. It is still Active — try again.`;
        }
    };

    if (boot === 'loading') {
        return (
            <CpmPageShell gap={22} maxWidth={1080} variant="compactBottom">
                <Breadcrumb />
                <NavLinks />
                <CpmVolumeTableSkeleton withHeaderCard />
            </CpmPageShell>
        );
    }

    if (boot === 'error') {
        return (
            <CpmPageShell gap={22} maxWidth={1080} variant="compactBottom">
                <Breadcrumb />
                <NavLinks />
                <CpmErrorCard
                    title="Couldn't load volume settings"
                    body="The volume list didn't come back. Nothing has been changed — the standard volumes offered on variant create are unaffected."
                    onRetry={() => void load('retry')}
                    retrying={bootRetrying}
                />
            </CpmPageShell>
        );
    }

    return (
        <CpmPageShell gap={22} maxWidth={1080} variant="compactBottom">
            <Breadcrumb />
            <NavLinks />

            {persistError ? (
                <div
                    className="flex items-center rounded-[12px] bg-[var(--cpm-shortfall-bg)] border border-[var(--cpm-shortfall-border)]"
                    style={{ gap: 11, padding: '13px 16px' }}
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-shortfall-text)"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                    >
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </svg>
                    <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
                        <span
                            className="text-[var(--cpm-shortfall-text)]"
                            style={{ fontSize: 13, fontWeight: 800 }}
                        >
                            Changes not saved
                        </span>
                        <span
                            className="text-[var(--cpm-shortfall-text-med)]"
                            style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45 }}
                        >
                            The volume list couldn&apos;t be written, so the Variant create dropdown
                            still shows the previous set.
                        </span>
                    </div>
                    <CpmButton
                        variant="danger"
                        size="sm"
                        disabled={persistRetrying}
                        leadingIcon={persistRetrying ? <span className="cpm-spin-w" /> : undefined}
                        onClick={() => void retryPersist()}
                        className="!shrink-0 !bg-[var(--cpm-shortfall-text)] !text-white !border-transparent"
                    >
                        {persistRetrying ? 'Retrying' : 'Retry save'}
                    </CpmButton>
                </div>
            ) : null}

            <div
                className="flex items-start flex-wrap rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)]"
                style={{
                    gap: 18,
                    padding: 20,
                    boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                }}
            >
                <div className="flex items-start min-w-0" style={{ gap: 13 }}>
                    <span
                        className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-brand-purple)]"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: 'var(--cpm-purple-tint-13)',
                        }}
                    >
                        <SlidersIcon size={19} />
                    </span>
                    <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
                        <h1 className="m-0 !text-[22px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                            Volume settings
                        </h1>
                        <p className="m-0 !text-[13.5px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                            Standard volumes offered when creating a variant.
                        </p>
                        <div className="flex items-center" style={{ gap: 9 }}>
                            <span
                                className="tabular-nums text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 12, fontWeight: 700 }}
                            >
                                {summary}
                            </span>
                            {syncing ? (
                                <span
                                    className="inline-flex items-center text-[var(--cpm-brand-purple)]"
                                    style={{ gap: 6, fontSize: 11.5, fontWeight: 800 }}
                                >
                                    <span
                                        className="cpm-spin"
                                        style={{ width: 11, height: 11, borderWidth: 1.8 }}
                                    />
                                    Syncing to the create dropdown
                                </span>
                            ) : null}
                            {syncedFlash ? (
                                <span
                                    className="inline-flex items-center text-[var(--cpm-success-text-light)]"
                                    style={{ gap: 6, fontSize: 11.5, fontWeight: 800 }}
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    Saved
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>
                <CpmButton
                    variant="navy"
                    size="modal"
                    className="!ml-auto"
                    isLoading={addOpening}
                    leadingIcon={
                        addOpening ? undefined : (
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 5v14" />
                                <path d="M5 12h14" />
                            </svg>
                        )
                    }
                    onClick={openAdd}
                >
                    {addOpening ? 'Opening' : 'Add volume'}
                </CpmButton>
            </div>

            {rows.length > 0 ? (
                <div
                    className="flex flex-col rounded-[16px] border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-card)]"
                    style={{
                        padding: '18px 20px 8px',
                        boxShadow:
                            '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
                    }}
                >
                    <div
                        className="cpm-table-head grid items-center border-b border-[var(--cpm-border-default)]"
                        style={{
                            gridTemplateColumns: GRID_COLS,
                            gap: 14,
                            padding: '0 14px 9px',
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'var(--cpm-text-primary)',
                            fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                        }}
                    >
                        <span className="justify-self-end">Value</span>
                        <span>Unit</span>
                        <span>Status</span>
                        <span>Created</span>
                        <span className="justify-self-end">Actions</span>
                    </div>

                    {rows.map((r) => {
                        const on = r.status === CPM_VOLUME_SETTING_STATUS.ACTIVE;
                        const phase = rowPhase[r._id];
                        const busy = phase === 'toggling' || phase === 'deleting';
                        const errored = phase === 'toggleError';
                        const opening = phase === 'opening';
                        return (
                            <div
                                key={r._id}
                                className="grid items-center border-b border-[var(--cpm-border-default)] rounded-[10px] transition-colors duration-150 ease-out hover:bg-[var(--cpm-surface-subtle)]"
                                style={{
                                    gridTemplateColumns: GRID_COLS,
                                    gap: 14,
                                    padding: '12px 14px',
                                    opacity: busy ? 0.65 : 1,
                                }}
                            >
                                <span
                                    className="justify-self-end tabular-nums tracking-[-0.01em]"
                                    style={{
                                        fontSize: 15,
                                        fontWeight: 800,
                                        color: on
                                            ? 'var(--cpm-text-primary)'
                                            : 'var(--cpm-text-tertiary)',
                                    }}
                                >
                                    {Number(r.value).toLocaleString('en-IN')}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{
                                        fontSize: 12.5,
                                        fontWeight: 700,
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    {volumeUnitShortLabel(r.unit)}
                                </span>
                                <span
                                    className="inline-flex items-center w-fit rounded-full border"
                                    style={{
                                        gap: 6,
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        fontWeight: 800,
                                        background: busy
                                            ? 'var(--cpm-surface-subtle)'
                                            : on
                                              ? 'var(--cpm-success-bg)'
                                              : '#fff',
                                        borderColor: busy
                                            ? 'var(--cpm-border-strong)'
                                            : on
                                              ? 'var(--cpm-success-border)'
                                              : 'var(--cpm-border-strong)',
                                        color: busy
                                            ? 'var(--cpm-text-secondary)'
                                            : on
                                              ? 'var(--cpm-success-text-light)'
                                              : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {busy ? (
                                        <span
                                            className="cpm-spin"
                                            style={{ width: 10, height: 10, borderWidth: 1.8 }}
                                        />
                                    ) : (
                                        <span
                                            className="rounded-full"
                                            style={{
                                                width: 7,
                                                height: 7,
                                                background: on
                                                    ? 'var(--cpm-success-dot)'
                                                    : 'var(--cpm-text-tertiary)',
                                            }}
                                        />
                                    )}
                                    {busy
                                        ? phase === 'deleting'
                                            ? 'DELETING'
                                            : 'SAVING'
                                        : r.status}
                                </span>
                                <span
                                    className="truncate text-[var(--cpm-text-tertiary)]"
                                    style={{ fontSize: 12, fontWeight: 700 }}
                                >
                                    {formatFlaggedDate(r.createdAt)}
                                </span>
                                <div
                                    className="justify-self-end flex items-center"
                                    style={{ gap: 8 }}
                                >
                                    <button
                                        type="button"
                                        title="Edit this volume"
                                        onClick={() => openEdit(r)}
                                        disabled={busy}
                                        className="inline-flex items-center rounded-[9px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] cursor-pointer hover:border-[var(--cpm-brand-purple)] hover:text-[var(--cpm-brand-navy)] disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            gap: 6,
                                            height: 32,
                                            padding: '0 11px',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            cursor: opening ? 'progress' : undefined,
                                        }}
                                    >
                                        {opening ? (
                                            <span
                                                className="cpm-spin"
                                                style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                            />
                                        ) : (
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                                            </svg>
                                        )}
                                        {opening ? 'Opening' : 'Edit'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleToggleStatus(r)}
                                        disabled={phase === 'deleting'}
                                        className="inline-flex items-center rounded-[9px] border-[1.5px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            gap: 6,
                                            height: 32,
                                            padding: '0 12px',
                                            fontSize: 12,
                                            fontWeight: 800,
                                            cursor: phase === 'toggling' ? 'progress' : 'pointer',
                                            borderColor: errored
                                                ? 'var(--cpm-shortfall-border)'
                                                : on
                                                  ? 'var(--cpm-warning-border)'
                                                  : 'var(--cpm-success-border)',
                                            background: errored
                                                ? 'var(--cpm-shortfall-bg)'
                                                : on
                                                  ? 'var(--cpm-warning-bg)'
                                                  : 'var(--cpm-success-bg)',
                                            color: errored
                                                ? 'var(--cpm-shortfall-text)'
                                                : on
                                                  ? 'var(--cpm-warning-text)'
                                                  : 'var(--cpm-success-text-light)',
                                            opacity: phase === 'deleting' ? 0.5 : 1,
                                        }}
                                    >
                                        {phase === 'toggling' ? (
                                            <span
                                                className="cpm-spin"
                                                style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                            />
                                        ) : null}
                                        {errored ? (
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.4"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                                <path d="M3 3v5h5" />
                                            </svg>
                                        ) : null}
                                        {phase === 'toggling'
                                            ? on
                                                ? 'Disabling'
                                                : 'Enabling'
                                            : errored
                                              ? 'Retry'
                                              : on
                                                ? 'Disable'
                                                : 'Enable'}
                                    </button>
                                    <button
                                        type="button"
                                        title="Delete this volume"
                                        onClick={() => setDeleting(r)}
                                        disabled={busy}
                                        className="inline-flex items-center justify-center rounded-[9px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white text-[var(--cpm-text-secondary)] cursor-pointer hover:border-[var(--cpm-shortfall-text)] hover:text-[var(--cpm-shortfall-text)] disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            opacity: phase === 'toggling' ? 0.5 : 1,
                                        }}
                                    >
                                        {phase === 'deleting' ? (
                                            <span
                                                className="cpm-spin"
                                                style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                            />
                                        ) : (
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M3 6h18" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6" />
                                                <path d="M14 11v6" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    <div
                        style={{
                            padding: '14px 4px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1.5,
                            color: 'var(--cpm-text-tertiary)',
                        }}
                    >
                        Inactive volumes stop appearing in the Variant create dropdown. Variants
                        already saved at that size keep their volume.
                    </div>
                </div>
            ) : (
                <div
                    className="flex flex-col items-center text-center rounded-[16px] border-[1.5px] border-dashed border-[var(--cpm-border-strong)] bg-[var(--cpm-surface-card)]"
                    style={{ gap: 14, padding: '44px 32px' }}
                >
                    <span
                        className="inline-flex items-center justify-center rounded-[18px] border border-[var(--cpm-border-default)] bg-white text-[var(--cpm-brand-purple)]"
                        style={{ width: 72, height: 72 }}
                    >
                        <SlidersIcon size={30} />
                    </span>
                    <h3 className="m-0 !text-[20px] !font-extrabold !tracking-[-0.02em] !text-[var(--cpm-brand-navy)]">
                        No standard volumes
                    </h3>
                    <p className="m-0 max-w-[520px] !text-[13.5px] !font-semibold !leading-[1.55] !text-[var(--cpm-text-secondary)]">
                        Nothing is offered in the Variant create dropdown until a volume is added
                        here. Existing variants are unaffected.
                    </p>
                    <CpmButton
                        variant="navy"
                        size="modal"
                        style={{ height: 42, paddingLeft: 20, paddingRight: 20 }}
                        isLoading={addOpening}
                        leadingIcon={
                            addOpening ? undefined : (
                                <svg
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 5v14" />
                                    <path d="M5 12h14" />
                                </svg>
                            )
                        }
                        onClick={openAdd}
                    >
                        {addOpening ? 'Opening' : 'Add the first volume'}
                    </CpmButton>
                </div>
            )}

            <VolumeFormModal
                open={formOpen}
                editing={editing}
                formPhase={formPhase}
                onClose={closeForm}
                onSave={handleSave}
                onRetryFetch={retryFormFetch}
            />

            <DeleteVolumeConfirm
                open={!!deleting}
                setting={deleting}
                onClose={() => setDeleting(null)}
                onConfirmDelete={handleConfirmDelete}
                onDisableInstead={handleDisableInstead}
            />
        </CpmPageShell>
    );
}

function Breadcrumb() {
    return (
        <div
            className="flex items-center"
            style={{
                gap: 8,
                fontSize: 12.5,
                fontWeight: 600,
                color: 'var(--cpm-text-secondary)',
            }}
        >
            <span>Admin</span>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <span>Catalog</span>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <Link
                to="/central-product-master"
                className="!text-[var(--cpm-text-secondary)] !no-underline hover:!text-[var(--cpm-brand-navy)]"
            >
                Central Product Master
            </Link>
            <span style={{ fontWeight: 100, color: 'var(--cpm-text-tertiary)' }}>/</span>
            <span className="!text-[var(--cpm-brand-navy)]" style={{ fontWeight: 800 }}>
                Volume Settings
            </span>
        </div>
    );
}

function NavLinks() {
    return (
        <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
            <Link
                to="/central-product-master"
                className="inline-flex items-center !no-underline !text-[var(--cpm-text-secondary)] rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white hover:!border-[var(--cpm-brand-purple)] hover:!text-[var(--cpm-brand-navy)]"
                style={{
                    gap: 8,
                    height: 34,
                    padding: '0 14px 0 11px',
                    fontSize: 12.5,
                    fontWeight: 800,
                }}
            >
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="m15 18-6-6 6-6" />
                </svg>
                Central Product Master
            </Link>
            <Link
                to="/central-product-master/master-editor?level=catalog&view=variants"
                className="inline-flex items-center !no-underline !text-[var(--cpm-text-secondary)] rounded-[10px] border-[1.5px] border-[var(--cpm-border-strong)] bg-white hover:!border-[var(--cpm-brand-purple)] hover:!text-[var(--cpm-brand-navy)]"
                style={{
                    gap: 7,
                    height: 34,
                    padding: '0 13px',
                    fontSize: 12.5,
                    fontWeight: 800,
                }}
            >
                Variants in Master Editor
            </Link>
        </div>
    );
}
